import { useState, useEffect } from 'react';
import * as React from 'react';
import { SaveClientFlags } from '@sdk/authentication/types';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import Button from '@sdk/components/ui/buttons/Button';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import gettext from '@sdk/lang/gettext';
import Column from '@sdk/components/ui/spacing/Column';

import { EXTERNAL_RATE_SERVICES } from '../../constants';
import PopupSurveyContainer from '../survey/PopupSurveyContainer';
import SurveyCloseButton from '../survey/SurveyCloseButton';
import SurveyStep from '../survey/SurveyStep';

import {
  getExternalRateService,
  setCountdownDate,
  setServiceRated,
  shouldDisplayExternalRate,
} from './utils';

type Props = {
  user: AuthUserType;
  usersCount: number;
  subscriptionName: string;
  saveClientFlags: SaveClientFlags;
  hasShopifyIntegration: boolean;
};

const ExternalRate: React.FC<React.PropsWithChildren<Props>> = ({
  user,
  saveClientFlags,
  hasShopifyIntegration,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const clientFlags = user?.settings?.clientFlags;

  const close = (wasRated?: boolean): void => {
    if (!wasRated) {
      setCountdownDate(clientFlags, saveClientFlags, true);
    }

    setIsVisible(false);
  };

  useEffect(() => {
    if (user && !isVisible && shouldDisplayExternalRate(clientFlags, hasShopifyIntegration)) {
      setIsVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!isVisible) {
    return null;
  }

  const serviceType = getExternalRateService(clientFlags, hasShopifyIntegration);
  const service = EXTERNAL_RATE_SERVICES[serviceType];

  const onRateClick = (): void => {
    if (service) {
      const tab = window.open(service.url, '_blank');
      tab.focus();
      setServiceRated(serviceType, clientFlags, saveClientFlags);
    }

    close(true);
  };

  return (
    <PopupSurveyContainer>
      <SurveyCloseButton onClose={close} />
      <SurveyStep
        title={gettext('Hope you are liking Sling so far!')}
        subtitle={gettext(
          'Please leave a review on %(platformName)s to spread the love and help other customers choose Sling.',
          { platformName: service.name }
        )}>
        <Column mTop={16}>
          <Button buttonType={ButtonType.primary} onClick={onRateClick}>
            {gettext('Review Sling')}
          </Button>
        </Column>
      </SurveyStep>
    </PopupSurveyContainer>
  );
};

export default ExternalRate;
